import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ContentChild,
  ElementRef,
  TemplateRef,
  effect,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  IconDefinition,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-carousel',
    templateUrl: './carousel.component.html',
    styleUrls: ['./carousel.component.scss'],
    imports: [FontAwesomeModule, NgClass, NgTemplateOutlet]
})
export class CarouselComponent implements AfterViewChecked {
  carousel = viewChild<ElementRef>('carousel');
  track = viewChild<ElementRef>('carouselTrack');

  @ContentChild(TemplateRef) template: TemplateRef<any>;

  value = input<any[]>([]);
  scrollBy = input<number | undefined>();
  hideArrows = input<boolean>(false);

  prevIcon = input<IconDefinition>(faChevronLeft);
  nextIcon = input<IconDefinition>(faChevronRight);

  _ITEM_SELECTOR = '.carousel-item';

  scrollLimit = signal(-1);
  allowScroll = signal(true);

  constructor() {
    effect(
      () => {
        this.allowScroll.set(
          this.track().nativeElement.scrollWidth >
            this.track().nativeElement.clientWidth
        );
      }
    );
  }

  ngAfterViewChecked(): void {
    this.allowScroll.set(
      this.track().nativeElement.scrollWidth >
        this.track().nativeElement.clientWidth
    );
  }

  private _getPosition(operation: 'prev' | 'next', track: any): number {
    const item = track.querySelector(this._ITEM_SELECTOR);
    const itemWidth: number = item.offsetWidth;

    const current = Math.ceil(track.scrollLeft / itemWidth) * itemWidth;

    if (operation === 'next')
      return current + itemWidth - (track.offsetWidth % itemWidth);

    if (operation === 'prev') return current - itemWidth;

    return current;
  }

  private _updateScrollLimits() {
    if (this.track().nativeElement.scrollLeft < 1) {
      this.scrollLimit.set(0);
      return;
    }
    if (
      Math.round(this.track().nativeElement.scrollLeft) >=
      this.track().nativeElement.scrollWidth -
        this.track().nativeElement.clientWidth
    ) {
      this.scrollLimit.set(1);
      return;
    }
    this.scrollLimit.set(0);
  }

  onNexItem() {
    const distanceToNextItem = this.track().nativeElement.querySelector(
      this._ITEM_SELECTOR
    ).offsetWidth;

    const showingItems = Math.floor(
      this.scrollBy() ??
        this.track().nativeElement.clientWidth / distanceToNextItem
    );

    const scrollBy = showingItems * distanceToNextItem;
    const currentPosition = this.track().nativeElement.scrollLeft;
    const scrollTo = currentPosition + scrollBy;

    const scrollToItem = Math.min(
      scrollTo - (scrollTo % distanceToNextItem),
      this.track().nativeElement.scrollWidth -
        this.track().nativeElement.clientWidth
    );

    this.track().nativeElement.scrollTo({
      left: scrollToItem,
      behavior: 'smooth',
    });

    this._updateScrollLimits();
  }

  onPrevItem() {
    const distanceToNextItem = this.track().nativeElement.querySelector(
      this._ITEM_SELECTOR
    ).offsetWidth;

    const showingItems = Math.floor(
      this.scrollBy() ??
        this.track().nativeElement.clientWidth / distanceToNextItem
    );

    const scrollBy = showingItems * distanceToNextItem;
    const currentPosition = this.track().nativeElement.scrollLeft;
    const scrollTo = currentPosition - scrollBy;

    const scrollToItem = Math.max(
      scrollTo - (scrollTo % distanceToNextItem),
      0
    );

    this.track().nativeElement.scrollTo({
      left: scrollToItem,
      behavior: 'smooth',
    });

    this._updateScrollLimits();
  }

  onScroll() {
    this._updateScrollLimits();
  }

  onResize() {
    this.allowScroll.set(
      this.track().nativeElement.scrollWidth >
        this.track().nativeElement.clientWidth
    );
    this._updateScrollLimits();
  }
}
